/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  PlusCircle,
  Search,
  Edit2,
  Trash2,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { accountService } from '../services/accountService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { usePermissions } from '../context/PermissionContext';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SEARCH_DEBOUNCE_MS = 500;
const DEFAULT_LIMIT = 10;

const INITIAL_FILTERS = {
  search: '',
  status: 'All',
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export default function Accounts() {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(INITIAL_FILTERS);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [serviceRequestTypes, setServiceRequestTypes] = useState([]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ limit: 100 });
        setServiceRequestTypes(data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadTypes();
  }, []);

  const paginationRef = useRef(pagination);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const requestIdRef = useRef(0);
  const fetchAccounts = useCallback(async (pageOverride) => {
    const currentRequestId = ++requestIdRef.current;
    const currentPagination = paginationRef.current;
    try {
      setLoading(true);
      const params = {
        page: pageOverride ?? currentPagination.page,
        limit: currentPagination.limit,
      };

      if (debouncedFilters.search) {
        params.search = debouncedFilters.search;
      }

      if (debouncedFilters.status && debouncedFilters.status !== 'All') {
        params.status = debouncedFilters.status.toLowerCase();
      }

      const { data } = await accountService.getAccounts(params);
      if (currentRequestId !== requestIdRef.current) return;

      const accountsList = data.data || [];
      const paginationData = data.pagination || {};
      const total = paginationData.total ?? data.total ?? accountsList.length;
      const limit = paginationData.limit ?? data.limit ?? currentPagination.limit;
      const totalPages = paginationData.totalPages ?? data.totalPages ?? Math.ceil(total / limit);

      setAccounts(accountsList);
      setPagination({
        page: paginationData.page ?? data.page ?? (pageOverride ?? currentPagination.page),
        limit,
        total,
        totalPages,
      });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      toastService.error(err.response?.data?.message || `Failed to load ${ACCOUNT_LABEL.pluralLower}`);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedFilters]);

  const isFirstFilterFetchRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterFetchRender.current) {
      isFirstFilterFetchRender.current = false;
      return;
    }
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, [debouncedFilters]);

  useEffect(() => {
    fetchAccounts(pagination.page);
  }, [pagination.page, fetchAccounts]);

  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, page }));
  };

  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accountService.deleteAccount(deleteTarget.id);
      toastService.success(`${ACCOUNT_LABEL.singular} deleted successfully`);
      setDeleteTarget(null);
      fetchAccounts(pagination.page);
    } catch (err) {
      toastService.error(err.response?.data?.message || `Failed to delete ${ACCOUNT_LABEL.singularLower}`);
    } finally {
      setDeleting(false);
    }
  };

  const getServiceRequestTypesDisplay = (account) => {
    if (account.serviceRequestTypes && account.serviceRequestTypes.length > 0) {
      return account.serviceRequestTypes.map((t) => t.name).join(', ');
    }
    if (account.serviceRequestTypeIds && account.serviceRequestTypeIds.length > 0) {
      const names = account.serviceRequestTypeIds
        .map((id) => serviceRequestTypes.find((t) => t.uid === id)?.name)
        .filter(Boolean);
      return names.length > 0 ? names.join(', ') : '—';
    }
    return '—';
  };

  const hasActiveFilters = Boolean(filters.search) || filters.status !== 'All';

  const canManage = can.accounts.update() || can.accounts.delete();

  return (
    <div className="w-full space-y-4 pb-2">
      {/* Hero Header */}
      <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {ACCOUNT_LABEL.plural}
            </h1>
            <p className="text-xs text-base-content/50">
              Create, configure, and manage system {ACCOUNT_LABEL.pluralLower} and associated {SERVICE_REQUEST_LABEL.singularLower} types.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            {can.accounts.create() && (
              <button
                type="button"
                onClick={() => navigate('/accounts/new')}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold shadow-sm hover:shadow transition-all"
              >
                <PlusCircle size={15} />
                <span>New {ACCOUNT_LABEL.singular}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter & Search Bar Card */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-base-content/10 space-y-3 relative z-30">
        <div className="grid grid-cols-12 gap-3">
          {/* Search Input */}
          <div className="relative col-span-12 sm:col-span-8 lg:col-span-9">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/40" size={16} />
            <input
              type="text"
              placeholder={`Search ${ACCOUNT_LABEL.pluralLower} by name, slug or external ID...`}
              aria-label={`Search ${ACCOUNT_LABEL.pluralLower} by name, slug or external ID`}
              className="w-full pl-10 pr-9 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/30 hover:text-base-content"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="col-span-12 sm:col-span-4 lg:col-span-3">
            <select
              aria-label="Filter by status"
              className="w-full px-3 py-2.5 bg-base-100 border border-base-300 rounded-xl text-xs sm:text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="All">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Active Filter Badges Bar */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-base-content/5 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-wider text-base-content/40 flex items-center gap-1 mr-1">
                <Filter size={11} />
                <span>Active:</span>
              </span>

              {filters.search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Search: "{filters.search}"</span>
                  <button
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, search: '' }))}
                    className="hover:text-error"
                    aria-label="Clear search filter"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {filters.status !== 'All' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-base-200 text-base-content/70 border border-base-content/5">
                  <span>Status: {filters.status === 'active' ? 'Active' : 'Inactive'}</span>
                  <button
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, status: 'All' }))}
                    className="hover:text-error"
                    aria-label="Clear status filter"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-bold text-base-content/40 hover:text-error transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main Table Card */}
      <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden p-0 relative z-10">
        {loading ? (
          <div className="p-6 space-y-4">
            <div className="h-6 w-32 bg-base-300/60 rounded animate-pulse"></div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-base-200/50 rounded-xl animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="py-16 px-6 flex flex-col items-center justify-center text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-base-200 flex items-center justify-center text-base-content/30 shadow-inner">
              <Building2 size={28} />
            </div>
            <div>
              <h3 className="text-base font-black text-base-content">
                {hasActiveFilters ? `No matching ${ACCOUNT_LABEL.pluralLower}` : `No ${ACCOUNT_LABEL.pluralLower} yet`}
              </h3>
              <p className="text-xs text-base-content/40 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search criteria or clearing active filters.'
                  : `Get started by creating your first ${ACCOUNT_LABEL.singularLower} record.`}
              </p>
            </div>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl font-bold text-xs"
              >
                Clear all filters
              </button>
            ) : can.accounts.create() ? (
              <button
                type="button"
                onClick={() => navigate('/accounts/new')}
                className="btn btn-sm btn-primary rounded-xl font-bold gap-2 text-xs"
              >
                <PlusCircle size={14} />
                <span>Create {ACCOUNT_LABEL.singular}</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full text-left">
              <thead>
                <tr className="bg-base-200/40 border-b border-base-content/5 text-[10px] font-black uppercase tracking-widest text-base-content/40">
                  <th className="py-3.5 px-5">{ACCOUNT_LABEL.singular} Name</th>
                  <th className="py-3.5 px-4">Slug</th>
                  <th className="py-3.5 px-4">External {ACCOUNT_LABEL.singular} ID</th>
                  <th className="py-3.5 px-4">{SERVICE_REQUEST_LABEL.singular} Types</th>
                  <th className="py-3.5 px-4">Created Date</th>
                  {canManage && <th className="py-3.5 px-5 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-base-content/5 text-xs">
                {accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-base-200/40 transition-colors group">
                    {/* Account Name */}
                    <td className="py-3.5 px-5 font-bold text-base-content group-hover:text-primary transition-colors">
                      {account.name}
                    </td>

                    {/* Slug */}
                    <td className="py-3.5 px-4">
                      <code className="text-xs font-mono font-bold bg-base-200 px-2 py-0.5 rounded-lg text-base-content/70">
                        {account.slug}
                      </code>
                    </td>

                    {/* External Account ID */}
                    <td className="py-3.5 px-4">
                      {account.externalAccountId ? (
                        <code className="text-xs font-mono font-bold bg-base-200 px-2 py-0.5 rounded-lg text-base-content/70">
                          {account.externalAccountId}
                        </code>
                      ) : (
                        <span className="text-base-content/40">—</span>
                      )}
                    </td>

                    {/* Service Request Types */}
                    <td className="py-3.5 px-4 text-base-content/70 font-medium">
                      {getServiceRequestTypesDisplay(account)}
                    </td>

                    {/* Created Date */}
                    <td className="py-3.5 px-4 text-base-content/70 font-medium whitespace-nowrap">
                      {account.createdAt ? (
                        <div
                          className="tooltip tooltip-top tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                          data-tip={formatDateTime(account.createdAt)}
                        >
                          <span className="cursor-default hover:text-base-content transition-colors">
                            {formatDate(account.createdAt)}
                          </span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>

                    {/* Actions */}
                    {canManage && (
                      <td className="py-3.5 px-5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {can.accounts.update() && (
                            <div
                              className="tooltip tooltip-left tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
                              data-tip="Edit"
                            >
                              <button
                                type="button"
                                onClick={() => navigate(`/accounts/${account.id}/edit`, { state: { account } })}
                                className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                                aria-label={`Edit ${ACCOUNT_LABEL.singular}`}
                              >
                                <Edit2 size={15} />
                              </button>
                            </div>
                          )}
                          {can.accounts.delete() && (
                            <div
                              className="tooltip tooltip-left tooltip-error before:text-xs before:font-semibold before:whitespace-nowrap"
                              data-tip="Delete"
                            >
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(account)}
                                className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                                aria-label={`Delete ${ACCOUNT_LABEL.singular}`}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Compact Table Footer with Pagination */}
        {!loading && accounts.length > 0 && (
          <div className="px-4 py-2.5 border-t border-base-content/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-base-content/60 font-medium">
              <span className="font-semibold text-base-content">
                {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-base-content">{pagination.total}</span> records
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center gap-2.5">
                <span className="text-xs text-base-content/50 font-medium">
                  Page <span className="font-semibold text-base-content">{pagination.page}</span> /{' '}
                  <span className="font-semibold text-base-content">{pagination.totalPages}</span>
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    disabled={pagination.page === 1 || loading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Previous Page"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                    className="p-1 rounded-lg bg-base-100 border border-base-content/10 text-base-content/60 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-25 disabled:hover:text-base-content/60 disabled:hover:border-base-content/10 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all shadow-2xs"
                    title="Next Page"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={`Delete ${ACCOUNT_LABEL.singular}`}
      >
        <div className="mb-6 space-y-3">
          <p className="text-base-content/70 text-sm leading-relaxed">
            Are you sure you want to delete {ACCOUNT_LABEL.singularLower}{' '}
            <strong className="text-base-content font-mono px-1.5 py-0.5 rounded bg-base-200">
              {deleteTarget?.name}
            </strong>?
          </p>
          <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-xs font-semibold">
            This action cannot be undone. All associated {SUB_ACCOUNT_LABEL.pluralLower} and data will be permanently removed.
          </div>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? 'Deleting...' : `Delete ${ACCOUNT_LABEL.singular}`}
          </button>
        </div>
      </Modal>
    </div>
  );
}
